import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { IoRefresh } from 'react-icons/io5';
import { IoCheckmarkCircle, IoCloseCircle } from 'react-icons/io5';
import { resetPassword } from '../api/auth.api';
import { generateSecurePassword } from '../utils/password.utils';
import { PasswordValidation } from '../types/auth.types';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';

export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [rotationCount, setRotationCount] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [currentRotation, setCurrentRotation] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [validation, setValidation] = useState<PasswordValidation>({
    hasMinLength: false,
    hasUpperCase: false,
    hasNumber: false,
    hasSpecialChar: false,
  });
  const [isLoading, setIsLoading] = useState(false);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      toast.error('Token de réinitialisation invalide');
      navigate('/login');
    }
  }, [token, navigate]);

  useEffect(() => {
    let intervalId: number;
    let totalRotation = 0;

    if (isGenerating) {
      intervalId = window.setInterval(() => {
        setCurrentRotation((prev) => {
          totalRotation += 24;
          if (totalRotation >= 720) {
            clearInterval(intervalId);
            setIsGenerating(false);
            return 0;
          }
          return (prev + 24) % 360;
        });
      }, 20);
    } else {
      setCurrentRotation(0);
    }

    return () => {
      if (intervalId) {
        window.clearInterval(intervalId);
      }
    };
  }, [isGenerating]);

  const handlePasswordChange = (value: string) => {
    setNewPassword(value);
    setValidation({
      hasMinLength: value.length >= 12,
      hasUpperCase: /[A-Z]/.test(value),
      hasNumber: /[0-9]/.test(value),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(value),
    });
  };

  const handleGeneratePassword = () => {
    if (!isGenerating) {
      setIsGenerating(true);
      setRotationCount((prev) => prev + 1);
      const generatedPassword = generateSecurePassword();
      setNewPassword(generatedPassword);
      setConfirmPassword(generatedPassword);
      setValidation({
        hasMinLength: true,
        hasUpperCase: true,
        hasNumber: true,
        hasSpecialChar: true,
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (
      !validation.hasMinLength ||
      !validation.hasUpperCase ||
      !validation.hasNumber ||
      !validation.hasSpecialChar
    ) {
      toast.error('Le mot de passe ne respecte pas toutes les exigences');
      return;
    }

    setIsLoading(true);
    try {
      await resetPassword({ token: token, new_password: newPassword });
      toast.success('Mot de passe réinitialisé avec succès');
      navigate('/login');
    } catch (error) {
      toast.error('Erreur lors de la réinitialisation du mot de passe');
    } finally {
      setIsLoading(false);
    }
  };

  const getPasswordStrength = () => {
    let strength = 0;
    if (validation.hasMinLength) strength += 25;
    if (validation.hasUpperCase) strength += 25;
    if (validation.hasNumber) strength += 25;
    if (validation.hasSpecialChar) strength += 25;
    return strength;
  };

  const getStrengthColor = () => {
    const strength = getPasswordStrength();
    if (strength <= 20) return 'bg-red-500';
    if (strength <= 40) return 'bg-orange-500';
    if (strength <= 60) return 'bg-yellow-500';
    if (strength <= 80) return 'bg-blue-500';
    return 'bg-green-500';
  };

  const getStrengthLabel = () => {
    const strength = getPasswordStrength();
    if (strength <= 20) return 'Très faible';
    if (strength <= 40) return 'Faible';
    if (strength <= 60) return 'Moyen';
    if (strength <= 80) return 'Fort';
    return 'Très fort';
  };

  const getRotation = () => {
    if (isGenerating) {
      return `rotate(${currentRotation}deg)`;
    }
    const baseRotation = rotationCount * 360;
    const hoverRotation = isHovered ? 360 : 0;
    return `rotate(${baseRotation + hoverRotation}deg)`;
  };

  return (
    <div className="flex h-full min-h-[inherit] w-full flex-col items-center justify-center gap-2 rounded-md px-4 py-2 text-center opacity-80">
      <section className="w-full max-w-[450px] px-4">
        <div className="w-full rounded-lg bg-white shadow dark:bg-neutralBG-dark">
          <div className="space-y-6 p-6">
            <div>
              <h1 className="text-center text-2xl font-normal text-neutralText dark:text-neutralText-dark">
                Réinitialisation du mot de passe
              </h1>
            </div>
            <form onSubmit={handleSubmit}>
              <Input
                type="password"
                label="Nouveau mot de passe"
                value={newPassword}
                onChange={handlePasswordChange}
                required={true}
                id="newPassword"
                name="newPassword"
              />
              <div className="my-4">
                <Input
                  type="password"
                  label="Confirmer le mot de passe"
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  required={true}
                  error={
                    confirmPassword !== '' && newPassword !== confirmPassword
                  }
                  errorMessage={
                    confirmPassword !== '' && newPassword !== confirmPassword
                      ? 'Les mots de passe ne correspondent pas'
                      : undefined
                  }
                  id="confirmPassword"
                  name="confirmPassword"
                />
              </div>
              <div className="my-6 space-y-4">
                <div className="text-left text-sm font-medium text-neutralText opacity-90 dark:text-neutralText-dark">
                  Exigences du mot de passe :
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    {validation.hasMinLength ? (
                      <IoCheckmarkCircle className="text-green-500" />
                    ) : (
                      <IoCloseCircle className="text-red-500" />
                    )}
                    <span
                      className={
                        validation.hasMinLength
                          ? 'text-green-500'
                          : 'text-red-500'
                      }
                    >
                      Minimum 12 caractères
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {validation.hasUpperCase ? (
                      <IoCheckmarkCircle className="text-green-500" />
                    ) : (
                      <IoCloseCircle className="text-red-500" />
                    )}
                    <span
                      className={
                        validation.hasUpperCase
                          ? 'text-green-500'
                          : 'text-red-500'
                      }
                    >
                      Au moins une majuscule
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {validation.hasNumber ? (
                      <IoCheckmarkCircle className="text-green-500" />
                    ) : (
                      <IoCloseCircle className="text-red-500" />
                    )}
                    <span
                      className={
                        validation.hasNumber ? 'text-green-500' : 'text-red-500'
                      }
                    >
                      Au moins un chiffre
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {validation.hasSpecialChar ? (
                      <IoCheckmarkCircle className="text-green-500" />
                    ) : (
                      <IoCloseCircle className="text-red-500" />
                    )}
                    <span
                      className={
                        validation.hasSpecialChar
                          ? 'text-green-500'
                          : 'text-red-500'
                      }
                    >
                      Au moins un caractère spécial
                    </span>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="mb-2 text-left text-sm text-neutralText opacity-90 dark:text-neutralText-dark">
                    Force du mot de passe :{' '}
                    <span
                      className={`font-medium ${getStrengthColor().replace('bg-', 'text-')}`}
                    >
                      {getStrengthLabel()}
                    </span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${getStrengthColor()}`}
                      style={{ width: `${getPasswordStrength()}%` }}
                    ></div>
                  </div>
                </div>
              </div>
              <Button
                type="button"
                onClick={handleGeneratePassword}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                endIcon={
                  <IoRefresh
                    className={`text-lg ${isGenerating ? '' : 'transition-all duration-300'}`}
                    style={{ transform: getRotation() }}
                  />
                }
                variant="text"
                className="mb-2"
              >
                Générer un mot de passe sécurisé
              </Button>
              <Button type="submit" disabled={isLoading} variant="primary">
                {isLoading
                  ? 'Réinitialisation en cours...'
                  : 'Réinitialiser le mot de passe'}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};
